import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Chip, IconButton, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { EEmailPermissionLevel, IEmailRecipient } from "../../../../../invoice/create/interfaces/invoice.interfaces";
import { colors, alphaVar } from "../../../../../ui/colors";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const permissionChipStyles = (level: EEmailPermissionLevel) =>
  level === EEmailPermissionLevel.CAN_PAY
    ? { bgcolor: colors.orange[100], color: colors.orange[700] }
    : { bgcolor: colors.blue[100], color: colors.blue[700] };

interface Props {
  recipients: IEmailRecipient[];
  isEditable: boolean;
  isSaving: boolean;
  needsCanPay?: boolean;
  onSave: (next: IEmailRecipient[]) => void;
}

/**
 * Admin editor for invoice recipients. Permissions can be rotated in place and new
 * recipients can be appended. Deletions are disabled by design (backend enforces the same rule).
 */
export const EmailRecipientsEditor = ({ recipients, isEditable, isSaving, needsCanPay = true, onSave }: Props) => {
  const [draft, setDraft] = useState<IEmailRecipient[]>(recipients);
  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPermission, setNewPermission] = useState<EEmailPermissionLevel>(EEmailPermissionLevel.CAN_PAY);

  const existingEmails = useMemo(() => new Set(recipients.map((r) => r.email?.toLowerCase())), [recipients]);
  const hasChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(recipients), [draft, recipients]);
  const hasCanPay = draft.some((r) => r.permissionLevel === EEmailPermissionLevel.CAN_PAY);
  const canSave = isEditing && hasChanges && (!needsCanPay || hasCanPay);

  const startEditing = () => {
    setDraft(recipients);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(recipients);
    setNewEmail("");
    setIsEditing(false);
  };

  const togglePermission = (email: string) => {
    setDraft((prev) =>
      prev.map((r) =>
        r.email === email
          ? {
              ...r,
              permissionLevel: r.permissionLevel === EEmailPermissionLevel.CAN_PAY ? EEmailPermissionLevel.CAN_VIEW : EEmailPermissionLevel.CAN_PAY,
            }
          : r,
      ),
    );
  };

  const addRecipient = () => {
    const email = newEmail?.trim()?.toLowerCase();
    if (!emailRegex?.test(email) || existingEmails.has(email) || draft?.some((r) => r?.email?.toLowerCase() === email)) return;
    setDraft((prev) => [...prev, { email, permissionLevel: newPermission }]);
    setNewEmail("");
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700] }}>
          Email Recipients
        </Typography>
        {isEditable && !isEditing && (
          <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>
            Edit
          </Button>
        )}
        {isEditing && (
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<CloseIcon />} onClick={cancelEditing} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="small" variant="contained" startIcon={<SaveIcon />} disabled={!canSave || isSaving} onClick={() => onSave(draft)}>
              Save
            </Button>
          </Stack>
        )}
      </Stack>

      {isEditing && needsCanPay && !hasCanPay && (
        <Typography variant="caption" sx={{ color: colors.warning?.[700] ?? colors.orange[700] }}>
          At least one recipient must have "Can Pay" permission because this invoice has client payment methods.
        </Typography>
      )}
      {isEditing && !needsCanPay && (
        <Typography variant="caption" sx={{ color: colors.gray[600] }}>
          Can Pay is not required because this invoice has no client payment methods.
        </Typography>
      )}

      <Stack spacing={1}>
        {draft.map((recipient) => (
          <Box
            key={recipient.email}
            sx={{
              p: 1.5,
              borderRadius: 12,
              bgcolor: "transparent",
              border: "1px solid transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: "350px",
              width: "fit-content",
              gap: 3,
              transition: "0.1s ease-in-out",
              "&:hover": { border: `1px solid ${colors.orange[200]}`, bgcolor: `${colors.orange[200]}15` },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
              <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                {recipient.email}
              </Typography>
            </Box>
            {isEditing ? (
              <Select
                size="small"
                value={recipient.permissionLevel}
                onChange={() => togglePermission(recipient.email)}
                sx={{ minWidth: 120, fontSize: "0.75rem" }}
              >
                <MenuItem value={EEmailPermissionLevel.CAN_PAY}>Can Pay</MenuItem>
                <MenuItem value={EEmailPermissionLevel.CAN_VIEW}>Can View</MenuItem>
              </Select>
            ) : (
              <Chip
                label={recipient.permissionLevel === EEmailPermissionLevel.CAN_PAY ? "Can Pay" : "Can View"}
                size="small"
                sx={{ ...permissionChipStyles(recipient.permissionLevel), fontWeight: 600, fontSize: "0.7rem" }}
              />
            )}
          </Box>
        ))}
      </Stack>

      {isEditing && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: alphaVar(colors.gray[50], 0.5) }}>
          <TextField
            size="small"
            placeholder="new@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ flex: 1 }}
            error={!!newEmail && (!emailRegex.test(newEmail.trim()) || draft.some((r) => r.email?.toLowerCase() === newEmail?.trim().toLowerCase()))}
          />
          <Select
            size="small"
            value={newPermission}
            onChange={(e) => setNewPermission(e.target.value as EEmailPermissionLevel)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={EEmailPermissionLevel.CAN_PAY}>Can Pay</MenuItem>
            <MenuItem value={EEmailPermissionLevel.CAN_VIEW}>Can View</MenuItem>
          </Select>
          <IconButton color="primary" onClick={addRecipient} disabled={!emailRegex.test(newEmail.trim())}>
            <AddIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};
